import { z } from "zod";
/**
 * Container field keys whose joined text carries a `<key>: ` label (the raw
 * `fields` values stay unlabelled; only `text` is labelled — ADR-0033).
 */
const CONTAINER_LABELLED_FIELDS = new Set(["path", "owner", "tags", "transcript", "summary"]);
/** Key prefix under which item metadata entries live in `fields`. */
const METADATA_PREFIX = "metadata.";
/**
 * Single shared join behind both engines (ADR-0033): keyword index (`textFallback`
 * Orama `searchable`) and semantic path (`catalogStore` embedding input) consume the
 * returned `text` verbatim, so they can never drift apart. `labelFor` maps a field
 * key to the `<label> ` prefix used for that value inside the text only — never
 * into `fields`; returning null/empty keeps the value bare.
 *
 * @param fields ordered granularity fields to join with " — ".
 * @param labelFor optional per-key text label (null/undefined = unlabelled).
 * @returns the joined text both search engines consume.
 */
function joinFields(fields, labelFor) {
    return Object.entries(fields)
        .map(([key, value]) => {
        const label = labelFor?.(key);
        return label ? `${label}: ${value}` : value;
    })
        .join(" — ");
}
/** Label rule for containers: only `owner`/`tags`/`transcript`/`summary` carry one. */
function containerLabel(key) {
    return CONTAINER_LABELLED_FIELDS.has(key) ? key : null;
}
/**
 * Label rule for items: `metadata.<key>` entries carry their bare key
 * (`metadata.author` → `author: `), mirroring the container labels.
 */
function itemLabel(key) {
    return key.startsWith(METADATA_PREFIX) ? key.slice(METADATA_PREFIX.length) : null;
}
/**
 * The effective transcript of one file (ADR-0035, GLOSSARY.md): its manual
 * override when present — even when empty — else its auto STT text. `at` is the
 * change signal Extract hashes into its idempotency key, and `manual` tells
 * whether the override won. `undefined` means the file carries no transcript at
 * all (no override and no STT text). The single implementation shared by search
 * indexing, the Extract pipeline and the transcript UI.
 *
 * Kept here (rather than in `container.ts`) so the module stays free of value
 * imports — `container.ts` already imports `searchTextSchema` from here, and a
 * value import back would close the cycle the node-import pin guards against.
 *
 * @param file the file whose transcript to resolve (only `data` is read).
 * @returns the effective text plus its `at`/source, or `undefined`.
 */
export function effectiveTranscript(file) {
    const manual = file.data?.manualText;
    if (manual)
        return { text: manual.text, at: manual.at, manual: true };
    const stt = file.data?.stt;
    if (!stt?.text)
        return undefined;
    return { text: stt.text, at: stt.at, manual: false };
}
/**
 * The effective transcript text of one audio file (ADR-0035, GLOSSARY.md): its
 * manual transcript when present — even when empty — else its auto STT text.
 */
function fileEffectiveTranscript(file) {
    return effectiveTranscript(file)?.text;
}
/**
 * Searchable text + raw granularity fields for a container (ADR-0033, amended
 * 2026-10-07 by ticket 03 and 2026-10-14 by ticket 07).
 *
 * `fields` is an ordered record of raw field values keyed by field name:
 * `name` first (strongest identifier — earliest tokens, tuned in ticket 03),
 * then the breadcrumb `path` (only when the caller supplies one — containers
 * only, ADR-0036 decision 3), `kind` (only when present), `description` (only
 * when non-empty), `owner`, `tags` (joined with ", "), `transcript` and
 * `summary` (transcript before summary — both kept, see the tuning notes in
 * the search-text-pipeline spec). Empty granularity fields are omitted
 * entirely rather than stored as "".
 *
 * `transcript` is the **merge** of both transcript sources, never one only
 * (ADR-0036 decision 4): the effective transcript of every audio file — its
 * manual transcript when present, else its STT text (ADR-0035) — joined across
 * files with a single space, then the `data.llmSummary.transcript` appended.
 * Both sources are concatenated rather than one preferred, because they can
 * carry different vocabulary and the LLM transcript must not be dropped; the
 * duplication when the summary was built from the same STT is accepted. Empty
 * pieces are skipped, and the field is omitted when both sources are empty.
 *
 * `text` is `fields` joined with " — " through the shared join helper;
 * `path`, `owner`, `tags`, `transcript` and `summary` carry their `key: ` label
 * inside `text` only. Granularity changes alter every container's `text`, so
 * stored embedding hashes mismatch once and each entry re-embeds on the next
 * load (catalogStore `embedMissing`). Barcode and short-UUID icon phrase are not
 * part of it; prompt prefixes live only in `embeddingService`.
 *
 * @param c container to extract from.
 * @param path optional breadcrumb label (`pathLabelOf`) for containers; the
 *   server and the browser both pass it so ancestor names are matchable. Items
 *   have no path — a placement's path is unstable (ADR-0036).
 * @returns the granularity fields and the joined text both search engines use.
 */
export function containerSearchableText(c, path) {
    const fields = {};
    fields.name = c.name;
    if (path)
        fields.path = path;
    if (c.kind)
        fields.kind = c.kind;
    if (c.description)
        fields.description = c.description;
    if (c.owner)
        fields.owner = c.owner;
    if (c.tags.length > 0)
        fields.tags = c.tags.join(", ");
    const transcriptParts = c.files
        .filter((file) => file.kind === "audio")
        .map(fileEffectiveTranscript)
        .filter((t) => typeof t === "string" && t.length > 0);
    const llm = c.data?.llmSummary;
    if (llm?.transcript)
        transcriptParts.push(llm.transcript);
    if (transcriptParts.length > 0)
        fields.transcript = transcriptParts.join(" ");
    if (llm?.data?.summary)
        fields.summary = llm.data.summary;
    return { fields, text: joinFields(fields, containerLabel) };
}
/**
 * Searchable text + raw granularity fields for an item (ADR-0033, amended
 * 2026-10-07 by ticket 03).
 *
 * `fields` is an ordered record of raw field values keyed by field name:
 * `name` first (strongest identifier — earliest tokens, tuned in ticket 03),
 * then `category`, `description` (only when non-empty), then one
 * `metadata.<key>` entry per string, non-empty metadata value — in the
 * metadata object's own key order. Non-string and empty metadata values are
 * omitted entirely rather than stored as "". Metadata values equal to the
 * item's own `barcode` are omitted too: the barcode stays exact-match via the
 * `barcode:` filter (ADR-0022) and must not leak into free text through
 * metadata — real catalogs duplicate it there.
 *
 * `text` is `fields` joined with " — " through the shared join helper;
 * `metadata.<key>` entries carry their bare key as a label inside `text` only
 * (`author: Michael Ende`), mirroring the container labels; everything else is
 * bare. Barcode and short-UUID icon phrase are not part of it; prompt
 * prefixes live only in `embeddingService`.
 *
 * @param item item to extract from.
 * @returns the granularity fields and the joined text both search engines use.
 */
export function itemSearchableText(item) {
    const fields = {};
    fields.name = item.name;
    fields.category = item.category;
    if (item.description)
        fields.description = item.description;
    for (const [key, value] of Object.entries(item.metadata)) {
        if (typeof value !== "string" || !value)
            continue;
        if (value === item.barcode)
            continue;
        fields[`${METADATA_PREFIX}${key}`] = value;
    }
    return { fields, text: joinFields(fields, itemLabel) };
}
/** Stable non-crypto hash of a text's embedding input (A7 diffing). */
export function hashString(input) {
    let h = 5381;
    for (let i = 0; i < input.length; i++) {
        h = ((h << 5) + h + input.charCodeAt(i)) | 0;
    }
    return String(h >>> 0);
}
/**
 * Searchable text snapshot schema version.
 * Bumped on breaking granularity changes that alter the `text` format.
 */
export const SEARCHABLE_TEXT_VERSION = 2;
/**
 * Server-maintained searchable-text snapshot (ADR-0036): the joined `text`,
 * the granularity `version` it was produced with, and its write timestamp.
 * Shared so the container and item payloads cannot drift apart.
 */
export const searchTextSchema = z.object({
    text: z.string(),
    version: z.number().int().nonnegative(),
    at: z.string().datetime(),
});
/**
 * The server-maintained searchable text for an entity, when the stored
 * `data.searchText` is present and its `version` matches the current extractor
 * (ADR-0036); otherwise undefined so callers fall back to live extraction.
 *
 * @param entity container or item whose `data.searchText` may hold a snapshot.
 * @returns the stored `text` when current, else undefined.
 */
export function effectiveSearchText(entity) {
    const st = entity.data?.searchText;
    return st && st.version === SEARCHABLE_TEXT_VERSION ? st.text : undefined;
}
//# sourceMappingURL=searchable-text.js.map